/**
 * PHARMAKON — Refonte Achats · Stock WP-24 : « type de produit → champs », browser twin of the
 * server CONFIG (spec §B5; UX 02_achats §5.3, OP-ACH-05…09; need N-08).
 *
 * Owner of the values: `apps/server/src/config/produit-type-champs.config.js` (read by the
 * product write path, the e-facture auto-create and the Dermo-Conseiller assignment). This file
 * is a twin, not an import, because the API image is built from apps/server alone; the server test
 * `src/__tests__/produit-type-champs.parity.test.js` imports both and fails on any drift — change
 * a value there first, then here. Read by the S2 panel « Créer le produit » (WP-24) and, with the
 * same field sets, the fiche produit.
 */

/** The seven product types, in the Prisma enum order (`TypeProduit`). */
export const TYPES_PRODUIT = Object.freeze([
  "MEDICAMENT",
  "PARAPHARMACIE",
  "COSMETIQUE",
  "CONSOMMABLE",
  "MATERIEL_MEDICAL",
  "COMPLEMENT_ALIMENTAIRE",
  "ORTHOPEDIE",
]);

/** The type-dependent fields (§5.3). */
export const CHAMP_TYPE = Object.freeze({
  DCI: "DCI",
  DOSAGE: "DOSAGE",
  CONTENANCE: "CONTENANCE",
  FORME: "FORME",
  LABORATOIRE: "LABORATOIRE",
  MARQUE: "MARQUE",
  DESCRIPTION: "DESCRIPTION",
  NUM_ENR: "NUM_ENR",
  REGLEMENTATION: "REGLEMENTATION",
  DERMO: "DERMO",
});

const C = CHAMP_TYPE;
const PARA = Object.freeze([C.CONTENANCE, C.MARQUE, C.DERMO]);
const B5 = Object.freeze([C.MARQUE, C.DESCRIPTION]);

/**
 * The type-dependent fields each type carries (server CONFIG `CHAMPS_PAR_TYPE`). Refonte WP-101
 * (CP §15.7, D-42): a Complément alimentaire carries a laboratoire, no longer a marque.
 */
export const CHAMPS_PAR_TYPE = Object.freeze({
  MEDICAMENT: Object.freeze([C.DCI, C.DOSAGE, C.FORME, C.LABORATOIRE, C.NUM_ENR, C.REGLEMENTATION]),
  PARAPHARMACIE: PARA,
  COSMETIQUE: PARA,
  COMPLEMENT_ALIMENTAIRE: Object.freeze([C.DOSAGE, C.FORME, C.LABORATOIRE, C.DERMO]),
  CONSOMMABLE: B5,
  MATERIEL_MEDICAL: B5,
  ORTHOPEDIE: B5,
});

/** The write-payload keys each field owns (server CONFIG `COLONNES_CHAMP`). */
export const COLONNES_CHAMP = Object.freeze({
  DCI: Object.freeze(["dci_id", "_dci_nom"]),
  DOSAGE: Object.freeze(["dosage"]),
  CONTENANCE: Object.freeze(["dosage"]),
  FORME: Object.freeze(["forme_id", "_forme_nom"]),
  LABORATOIRE: Object.freeze(["laboratoire_id", "_laboratoire_nom"]),
  MARQUE: Object.freeze(["marque"]),
  DESCRIPTION: Object.freeze(["description"]),
  NUM_ENR: Object.freeze(["num_enr_cnas"]),
});

/** Refonte WP-101 (D-42) — the two producer fields (server CONFIG `CHAMPS_PRODUCTEUR`). */
export const CHAMPS_PRODUCTEUR = Object.freeze([C.LABORATOIRE, C.MARQUE]);

/**
 * Refonte WP-101 (D-42, A-113) — the stored producer columns a write never clears when the type does
 * not carry them (server CONFIG `COLONNES_HISTORIQUE_PRODUCTEUR`): shown read-only by the fiche.
 */
export const COLONNES_HISTORIQUE_PRODUCTEUR = Object.freeze(["laboratoire_id", "marque"]);

/** Does a product of `type` carry `champ`? Unknown type → the Médicament set. */
export function typePorte(type, champ) {
  return (CHAMPS_PAR_TYPE[type] ?? CHAMPS_PAR_TYPE.MEDICAMENT).includes(champ);
}

/**
 * Refonte WP-67 (A-77, D-30) — the behaviour key of a catalogue type (server `typeBase`): a type of the
 * officine's catalogue answers its `type_base`, a bare TypeProduit value answers itself.
 */
export function typeBase(type) {
  if (type == null) return null;
  if (typeof type === "string") return type;
  return type.type_base ?? null;
}

/** A-77 — the type-dependent fields of a catalogue type: its base type's (server `champsDuType`). */
export function champsDuType(type) {
  return CHAMPS_PAR_TYPE[typeBase(type)] ?? CHAMPS_PAR_TYPE.MEDICAMENT;
}

/**
 * Refonte WP-101 (CP §15.6 / §15.7) — the producer field of a catalogue type (server
 * `producteurDuType`): LABORATOIRE, MARQUE, or null for an unknown base.
 */
export function producteurDuType(type) {
  const base = typeBase(type);
  if (!base || !CHAMPS_PAR_TYPE[base]) return null;
  return CHAMPS_PRODUCTEUR.find((c) => CHAMPS_PAR_TYPE[base].includes(c)) ?? null;
}

/** WP-67 / WP-69 — may the type carry a default margin %? Not a « Réglementation » base (server `margeAdmise`). */
export function margeAdmise(type) {
  const base = typeBase(type);
  return base != null && !typePorte(base, C.REGLEMENTATION);
}

/**
 * Refonte WP-67 (M-23) — bounds of « Paramètres › Achats et étiquettes › Types de produits » (server
 * `TYPE_PRODUIT_OFFICINE_BORNES`, config/type-produit-officine.config.js).
 */
export const TYPES_PRODUIT_OFFICINE = Object.freeze({
  LIBELLE_MIN: 2,
  LIBELLE_MAX: 60,
  TYPES_MAX: 50,
  MARGE_PCT_MIN: 0,
  MARGE_PCT_MAX: 999.99,
});

/** Types whose products may be placed in the Dermo-Conseiller (OP-ACH-07). */
export const TYPES_DERMO = Object.freeze(TYPES_PRODUIT.filter((t) => typePorte(t, C.DERMO)));

/**
 * TVA proposed by type (server `DEFAULT_TVA_BY_TYPE`, without its env overrides); a type absent
 * here has no default: the operator chooses (OP-ACH-08).
 */
export const TVA_DEFAUT_PAR_TYPE = Object.freeze({ MEDICAMENT: 0, PARAPHARMACIE: 19, COSMETIQUE: 19 });

/** Description bound (server `PRODUIT_DESCRIPTION.LONGUEUR_MAX`; OP-ACH-09). */
export const DESCRIPTION_LONGUEUR_MAX = 500;

/** Nom commercial bounds (server validator `nom_commercial: txt(300, 2)`). */
export const NOM_COMMERCIAL = Object.freeze({ MIN: 2, MAX: 300 });

/** Ceiling of a product amount — PPA, T.R (server validator `moneyDA`: ≤ 100 000 DA, centimes). */
export const PRODUIT_MONTANT_MAX = 100_000;

/** Barcodes per product (server CONFIG `CODES_BARRE_PRODUIT.MAX_PAR_PRODUIT`, A-08). */
export const CODES_BARRE_MAX_PAR_PRODUIT = 10;

/** Create request id upper bound (server CONFIG `CREATION_REQUETE_ID_MAX`; N-09). */
export const CREATION_REQUETE_ID_MAX = 64;
